import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom-v5-compat';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import useTableState from 'dashboard/lib/hooks/useTableState';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import { EnvironmentStatus } from 'dashboard/models/environment';
import { ProductFeatureName } from 'dashboard/models/organization';
import { NavItem, Role, RolePermission } from 'dashboard/models/role';
import { TriggerType } from 'dashboard/models/trigger';
import { User } from 'dashboard/models/user';
import { showMessage } from 'dashboard/redux/actions/messages';
import { clearRole, setRole } from 'dashboard/redux/actions/role';
import { useListTransactionTypesQuery } from 'dashboard/redux/api/transaction-type';
import { getAllTemplates } from 'dashboard/redux/selectors/organization';
import {
  setCurrentTableView as setApiLogsCurrentTableView,
  setTableViews as setApiLogsTableViews,
} from 'dashboard/redux/slices/api-logs';
import {
  setCurrentTableView as setWbheCurrentTableView,
  setTableViews as setWbheTableViews,
} from 'dashboard/redux/slices/webhook-events';
import { RootState } from 'dashboard/redux/store';
import { loadAccountTypes } from 'dashboard/redux/thunk-actions/account-types';
import { loadCaseAssignmentPoliciesList } from 'dashboard/redux/thunk-actions/case-assignment-policies';
import { loadCaseQueuesList } from 'dashboard/redux/thunk-actions/case-queues';
import { loadCaseTemplatesList } from 'dashboard/redux/thunk-actions/case-templates';
import { refreshSession } from 'dashboard/redux/thunk-actions/current-user-session';
import { loadExternalIntegrations } from 'dashboard/redux/thunk-actions/external-integrations';
import { loadInquiryTemplatesList } from 'dashboard/redux/thunk-actions/inquiry-templates';
import { loadLists } from 'dashboard/redux/thunk-actions/list';
import { loadReportTemplatesList } from 'dashboard/redux/thunk-actions/report-templates';
import { loadPaginatedRoleUserList, loadRole } from 'dashboard/redux/thunk-actions/role';
import { loadAllRoles } from 'dashboard/redux/thunk-actions/roles';
import { loadGenericTableViews, loadTableViews } from 'dashboard/redux/thunk-actions/table-views';
import { loadTeams } from 'dashboard/redux/thunk-actions/teams';
import { loadPaginatedUserList } from 'dashboard/redux/thunk-actions/user';
import { loadVerificationTemplatesList } from 'dashboard/redux/thunk-actions/verification-templates';
import { loadWorkflows } from 'dashboard/redux/thunk-actions/workflows';
import { SupportedWorkflowTrigger } from 'dashboard/routes/DashboardWorkflowsRoute/DashboardWorkflowsRoute';
import DashboardRolesShowView from 'dashboard/views/DashboardRolesShowView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useDebounced from 'lib/hooks/useDebounced';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import useTable from 'lib/hooks/useTable';
import { ApiEndpoint, ApiModel, ApiPayload, PaginationParams } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

type DashboardUserRolesShowRouteProps = PropsFromRedux;

type Params = {
  roleId: string;
};

const DashboardUserRolesShowRoute = (props: DashboardUserRolesShowRouteProps) => {
  useOnMountAndUnmount(props.clearRole);

  const navigate = useNavigate();
  const { roleId } = useParams<Params>() as Params; // roleId will always be defined on this route

  const [addUserToRoleSearchUsers, setAddUserToRoleSearchUsers] = useState<User[]>([]);
  const { tableState, paginationMeta, setPaginationMeta } = useTableState({});
  const { handlePageLinkClick, handlePageSizeClick } = useTable(tableState);
  const [addUserToRoleSearchTerm, setAddUserToRoleSearchTerm] = useState('');
  const [usersListSearchTerm, setUserSearchTerm] = useState('');
  const [debouncedSetUserSearchTerm] = useDebounced(setUserSearchTerm, 500, {
    leading: false,
    trailing: true,
  });

  const productFeatureIsDisabled = productFeatureDisabled(
    props.organization,
    ProductFeatureName.RolesDashboard,
    props.currentEnvironment?.type,
  );

  const { loading: loadingRole, callback: loadRole } = useApiLoader({
    loader: () => props.loadRole(roleId),
    deps: [props.loadRole, roleId],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load role.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const {
    loading: loadingUpdatingRole,
    error: updateRoleError,
    callback: updateRole,
  } = useApiCallback(
    async (
      role: Role,
      rolePermissions: RolePermission[],
      navItems: NavItem[],
      name: string,
      description: string,
    ) => {
      const response = await SessionClientDashboard.updateResource<Role>({
        id: role.id,
        type: ApiModel.Role,
        attributes: {
          'role-permissions': rolePermissions,
          name,
          preferences: {
            'enabled-nav-items': navItems,
          },
          description,
        },
      });

      //refresh current session to re-render components with hooks if current user role's permissions change
      if (
        props.currentUser.relationships.roles.data.map((role) => role.id).includes(props.role.id) &&
        SessionClientDashboard.refreshToken
      ) {
        try {
          await props.refreshSession(SessionClientDashboard.refreshToken);
        } catch (e) {
          navigate('/dashboard/login');
        }
      }

      props.setRole(response.data);
      props.showMessage('Role has been successfully updated', MessageType.Success);
    },
    [loadRole, props.showMessage, props.currentUser, props.role],
  );

  useApiErrorHandler({
    error: updateRoleError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage("Error when updating role's permissions.", MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'Sorry, something went wrong. Please try again later or contact support.',
        MessageType.Failure,
      ),
  });

  const { loading: loadingCaseAssignmentPolicies } = useApiLoader({
    loader: async () => {
      await props.loadCaseAssignmentPoliciesList({
        currentPage: 1,
        pageSize: 100,
        sortParams: { sort: '-', shouldSortDescending: false },
        filters: {},
      });
    },
    deps: [
      props.loadCaseAssignmentPoliciesList,
      props.showMessage,
      loadingRole,
      loadingUpdatingRole,
    ],
    options: { defaultLoading: true },
    skip: loadingRole || loadingUpdatingRole,
    onError: (error) => {
      if (!isPermissionsError(error) && error.status !== 404) {
        props.showMessage('Failed to load case assignment policies.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: loadingCaseTemplates } = useApiLoader({
    loader: async () => {
      await props.loadCaseTemplatesList({
        currentPage: 1,
        pageSize: 100,
        sortParams: { sort: '-', shouldSortDescending: false },
        filters: {},
      });
    },
    deps: [props.loadCaseTemplatesList, props.showMessage, loadingRole, loadingUpdatingRole],
    options: { defaultLoading: true },
    skip: loadingRole || loadingUpdatingRole,
    onError: (error) => {
      if (!isPermissionsError(error) && error.status !== 404) {
        props.showMessage('Failed to load case templates.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: loadingCaseQueues } = useApiLoader({
    loader: async () => {
      await props.loadCaseQueuesList({
        currentPage: 1,
        pageSize: 100,
        sortParams: { sort: '-', shouldSortDescending: false },
        filters: {},
      });
    },
    deps: [props.loadCaseQueuesList, props.showMessage, loadingRole, loadingUpdatingRole],
    options: { defaultLoading: true },
    skip: loadingRole || loadingUpdatingRole,
    onError: (error) => {
      if (!isPermissionsError(error) && error.status !== 404) {
        props.showMessage('Failed to load case queues.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: loadingInquiryTemplates } = useApiLoader({
    loader: async () => {
      await props.loadInquiryTemplatesList({
        currentPage: 1,
        pageSize: 100,
        sortParams: { sort: '-', shouldSortDescending: false },
        filters: {},
      });
    },
    deps: [props.loadInquiryTemplatesList, props.showMessage, loadingRole, loadingUpdatingRole],
    options: { defaultLoading: true },
    skip: loadingRole || loadingUpdatingRole,
    onError: (error) => {
      if (!isPermissionsError(error) && error.status !== 404) {
        props.showMessage('Failed to load inquiry templates.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const {
    loading: loadingRoleUsers,
    error: loadingRoleUsersError,
    callback: loadRoleUsers,
  } = useApiCallback(async () => {
    await props.loadPaginatedRoleUserList(
      roleId,
      tableState.currentPage,
      tableState.pageSize,
      setPaginationMeta,
      { omni: usersListSearchTerm },
    );
  }, [
    props.loadPaginatedRoleUserList,
    tableState.currentPage,
    tableState.pageSize,
    usersListSearchTerm,
    roleId,
    props.showMessage,
  ]);

  useApiErrorHandler({
    error: loadingRoleUsersError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load users.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiLoader({
    loader: async () => {
      const { data: users } = await SessionClientDashboard.listResource<User, PaginationParams>(
        ApiEndpoint.User,
        {
          page: {
            number: 1,
            size: 50,
          },
          filter: {
            omni: addUserToRoleSearchTerm,
          },
        },
      );

      setAddUserToRoleSearchUsers(users);
    },
    deps: [
      props.loadPaginatedUserList,
      tableState.currentPage,
      tableState.pageSize,
      addUserToRoleSearchTerm,
      props.showMessage,
    ],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load users.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: destroyRoleError, callback: destroyRole } = useApiCallback(
    async (role: string) => {
      const response = await SessionClientDashboard.deleteResource<Role>(ApiEndpoint.Role, role);

      navigate('/dashboard/user-roles');
      props.setRole(response.data);
      props.showMessage(
        `Successfully deactivated the role ${response.data.attributes.name}`,
        MessageType.Success,
      );
    },
    [props.showMessage],
  );

  useApiErrorHandler({
    error: destroyRoleError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Error when deactivating the role.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'Sorry, something went wrong. Please try again later or contact support.',
        MessageType.Failure,
      ),
  });

  const { error: removeUserFromRoleError, callback: removeUserFromRole } = useApiCallback(
    async ({ user, role }: { user: User; role: Role }) => {
      const response = await SessionClientDashboard.postResourceAction<
        Role,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ApiPayload<unknown, ApiModel.Role>
      >(
        {
          id: role.id,
          type: ApiModel.Role,
        },
        'remove-user',
        {
          meta: {
            'user-id': user.id,
          },
        },
      );

      props.setRole(response.data);
      props.showMessage(
        `Successfully removed ${user.attributes['email-address']} from the role.`,
        MessageType.Success,
      );

      if (user.id === props.currentUser.id) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        await props.refreshSession(SessionClientDashboard.refreshToken);
      }
    },
    [props.showMessage],
  );

  useApiErrorHandler({
    error: removeUserFromRoleError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Error when removing the user.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'Sorry, something went wrong. Please try again later or contact support.',
        MessageType.Failure,
      ),
  });

  const { error: addUsersToRoleError, callback: addUsersToRole } = useApiCallback(
    async (userIds: string[]) => {
      const response = await SessionClientDashboard.postResourceAction<
        Role,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ApiPayload<unknown, ApiModel.Role>
      >(
        {
          id: props.role.id,
          type: ApiModel.Role,
        },
        'add-users',
        {
          meta: {
            'user-ids': userIds,
          },
        },
      );

      props.setRole(response.data);
      props.showMessage(
        userIds.length === 1
          ? 'Successfully added user to the role.'
          : `Successfully added ${userIds.length} users to the role.`,
        MessageType.Success,
      );

      if (userIds.includes(props.currentUser.id)) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        await props.refreshSession(SessionClientDashboard.refreshToken);
      }
    },
    [props.showMessage, props.role, props.currentUser],
  );

  useApiErrorHandler({
    error: addUsersToRoleError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Error when added the users.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'Sorry, something went wrong. Please try again later or contact support.',
        MessageType.Failure,
      ),
  });

  const { loading: loadingWorkflows } = useApiLoader({
    loader: async () => {
      await props.loadWorkflows({ triggerTypes: SupportedWorkflowTrigger as TriggerType[] });
    },
    deps: [props.loadWorkflows, props.showMessage, loadingRole, loadingUpdatingRole],
    options: { defaultLoading: true },
    skip: loadingRole || loadingUpdatingRole,
    onError: (error) => {
      if (!isPermissionsError(error) && error.status !== 404) {
        showMessage('Failed to load workflows, please try again');
      }
    },
    onNetworkError: () => showMessage('Failed to load workflows, please try again'),
  });

  const { loading: loadingLists } = useApiLoader({
    loader: async () => {
      await props.loadLists({
        // environment-id filters are deprecated
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        currentEnvironment: null,
        filters: {},
        sortParams: { sort: '-', shouldSortDescending: false },
      });
    },
    deps: [props.loadLists, props.showMessage, loadingRole, loadingUpdatingRole],
    options: { defaultLoading: true },
    skip: loadingRole || loadingUpdatingRole,
    onError: (error) => {
      if (!isPermissionsError(error) && error.status !== 404) {
        showMessage('Failed to load lists, please try again', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  const { loading: loadingAccountTypes } = useApiLoader({
    loader: async () => {
      await props.loadAccountTypes();
    },
    deps: [props.loadAccountTypes, props.showMessage, loadingRole, loadingUpdatingRole],
    options: { defaultLoading: true },
    skip: loadingRole || loadingUpdatingRole,
    onError: (error) => {
      if (!isPermissionsError(error) && error.status !== 404) {
        showMessage('Failed to load account types.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  const {
    isLoading: loadingTxnTypes,
    data: { data: transactionTypes = [] } = {},
    error: transactionTypesError,
  } = useListTransactionTypesQuery({});
  useRTKErrorHandler({
    error: transactionTypesError,
    onError: () => ({ message: 'Failed to load transaction types.', type: MessageType.Failure }),
  });

  const { loading: loadingTableViews } = useApiLoader({
    loader: async () => {
      await Promise.all([
        props.loadTableViews({ filters: { tableKey: 'cases_index' } }),
        props.loadGenericTableViews({
          filters: { tableKey: 'wbhe_index' },
          setCurrentTableView: setWbheCurrentTableView,
          setTableViews: setWbheTableViews,
        }),
        props.loadGenericTableViews({
          filters: { tableKey: 'api_logs_index' },
          setCurrentTableView: setApiLogsCurrentTableView,
          setTableViews: setApiLogsTableViews,
        }),
      ]);
    },
    deps: [props.loadTableViews, props.showMessage, loadingRole, loadingUpdatingRole],
    options: { defaultLoading: true },
    skip: loadingRole || loadingUpdatingRole,
    onError: (error) => {
      if (!isPermissionsError(error) && error.status !== 404) {
        props.showMessage('Failed to load table views.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: loadingTeams } = useApiLoader({
    loader: async () => {
      await props.loadTeams();
    },
    deps: [props.loadTeams, props.showMessage, loadingRole, loadingUpdatingRole],
    options: { defaultLoading: true },
    skip: loadingRole || loadingUpdatingRole,
    onError: (error) => {
      if (!isPermissionsError(error) && error.status !== 404) {
        props.showMessage('Failed to load teams.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: loadingExternalIntegrations } = useApiLoader({
    loader: async () => {
      await props.loadExternalIntegrations();
    },
    deps: [props.loadExternalIntegrations, props.showMessage, loadingRole, loadingUpdatingRole],
    options: { defaultLoading: true },
    skip: loadingRole || loadingUpdatingRole,
    onError: (error) => {
      if (!isPermissionsError(error) && error.status !== 404) {
        props.showMessage('Failed to load connections, please try again', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: loadingVerificationTemplates } = useApiLoader({
    loader: async () => {
      await props.loadVerificationTemplatesList({
        currentPage: 1,
        pageSize: 100,
        sortParams: { sort: '-', shouldSortDescending: false },
        filters: {},
      });
    },
    deps: [
      props.loadVerificationTemplatesList,
      props.showMessage,
      loadingRole,
      loadingUpdatingRole,
    ],
    options: { defaultLoading: true },
    skip: loadingRole || loadingUpdatingRole,
    onError: (error) => {
      if (!isPermissionsError(error) && error.status !== 404) {
        props.showMessage('Failed to load verification templates.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useEffect(() => {
    loadRoleUsers();
  }, [loadRoleUsers]);

  const handleSubmitAddUser = useCallback(
    async (userIds: string[]) => {
      await addUsersToRole(userIds);
      loadRoleUsers();
    },
    [addUsersToRole, loadRoleUsers],
  );

  const tableViews = useMemo(
    () => [...props.tableViews, ...props.wbheTableViews, ...props.apiLogsTableViews],
    [props.tableViews, props.wbheTableViews, props.apiLogsTableViews],
  );

  return (
    <DashboardRolesShowView
      addUserToRoleSearchUsers={addUserToRoleSearchUsers}
      caseAssignmentPolicies={props.caseAssignmentPolicies}
      caseTemplates={props.caseTemplates}
      caseQueues={props.caseQueues}
      environments={props.environments.filter(
        (e) => e.attributes.status === EnvironmentStatus.Active,
      )}
      organization={props.organization}
      currentUser={props.currentUser}
      inquiryTemplates={props.inquiryTemplates}
      isLoading={
        loadingRole ||
        loadingCaseAssignmentPolicies ||
        loadingCaseTemplates ||
        loadingCaseQueues ||
        loadingExternalIntegrations ||
        loadingInquiryTemplates ||
        loadingLists ||
        loadingTableViews ||
        loadingTeams ||
        loadingAccountTypes ||
        loadingTxnTypes ||
        loadingVerificationTemplates ||
        loadingWorkflows
      }
      externalIntegrations={props.externalIntegrations}
      lists={props.lists}
      loadingRoleUsers={loadingRoleUsers}
      productFeatureIsDisabled={productFeatureIsDisabled}
      reportTemplates={props.reportTemplates}
      role={props.role}
      roles={props.roles}
      tableViews={tableViews}
      teams={props.teams}
      accountTypes={props.accountTypes}
      transactionTypes={transactionTypes}
      roleUsers={props.roleUsers}
      usersListSearchTerm={usersListSearchTerm}
      verificationTemplates={props.verificationTemplates}
      workflows={props.workflows}
      setUserSearchTerm={debouncedSetUserSearchTerm}
      setAddUserToRoleSearchTerm={setAddUserToRoleSearchTerm}
      removeUserFromRole={removeUserFromRole}
      updateRole={updateRole}
      pageSize={tableState.pageSize}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      paginationMeta={paginationMeta}
      handlePageLinkClick={handlePageLinkClick}
      handlePageSizeClick={handlePageSizeClick}
      handleSubmitAddUser={handleSubmitAddUser}
      destroyRole={destroyRole}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      readmeJwtToken={props.readmeJwtToken}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    caseAssignmentPolicies: state.caseAssignmentPolicies.caseAssignmentPolicies,
    caseTemplates: state.caseTemplates.caseTemplates,
    caseQueues: state.caseQueues.caseQueues,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    currentUser: state.currentUser.currentUser,
    environments: state.organization.environments,
    featureFlags: state.featureFlags.featureFlags,
    externalIntegrations: state.externalIntegrations.externalIntegrations,
    inquiryTemplates: getAllTemplates(state),
    lists: state.lists.lists,
    organization: state.organization.organization,
    reportTemplates: state.reportTemplates.reportTemplates,
    role: state.role.role,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    roles: state.roles.allRoles,
    roleUsers: state.role.users,
    tableViews: state.tableViews.tableViews,
    wbheTableViews: state.webhookEvents.tableViews.views,
    apiLogsTableViews: state.apiLogs.tableViews.views,
    teams: state.teams.teams,
    accountTypes: state.accountTypes.accountTypes,
    verificationTemplates: state.verificationTemplates.verificationTemplates,
    workflows: state.workflows.workflows,
    readmeJwtToken: state.currentUser.readmeJwtToken,
  };
};

const mapDispatchToProps = {
  clearRole,
  setRole,
  loadRole,
  loadAllRoles,
  loadPaginatedUserList,
  loadPaginatedRoleUserList,
  loadCaseAssignmentPoliciesList,
  loadCaseTemplatesList,
  loadCaseQueuesList,
  loadExternalIntegrations,
  loadInquiryTemplatesList,
  loadLists,
  loadTableViews,
  loadGenericTableViews,
  loadTeams,
  loadAccountTypes,
  loadVerificationTemplatesList,
  loadReportTemplatesList,
  loadWorkflows,
  refreshSession,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardUserRolesShowRoute);
